import { notEmpty, theme } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { NextPage, PageProps } from "next";
import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { useAdSlotWithRefresh } from "scmp-app/components/advertisement/ad-slots/ad-slot/hooks";
import { PageSeo } from "scmp-app/components/meta";
import { SearchContextProvider } from "scmp-app/components/search/contexts";
import { SearchExploreTopics } from "scmp-app/components/search/search-explore-topics";
import { SearchInput } from "scmp-app/components/search/search-input";
import { SearchResultPanel } from "scmp-app/components/search/search-result-panel";
import { trackingAtom } from "scmp-app/components/tracking";
import { parseQueryString } from "scmp-app/lib/utils";

import { AdContainer, SearchContainer, StyledAdSlot } from "./styles";

type Props = PageProps & { value: string };

const SearchPage: NextPage<Props> = ({ value: routeQueryValue }) => {
  const { props: adSlotProps, refresh: refreshAdSlot } = useAdSlotWithRefresh({
    adUnit: "d_banner2a",
    breakpoint: theme.breakpoints.up("desktop"),
    sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
    withBackground: true,
    zone: "search/home",
  });

  const [currentValue, setCurrentValue] = useState(routeQueryValue);
  const [pendingValue, setPendingValue] = useState("");
  const [isShowTopics, setIsShowTopics] = useState(false);

  useEffect(() => {
    setCurrentValue(routeQueryValue);
    setPendingValue(routeQueryValue);
  }, [routeQueryValue]);

  useEffect(() => {
    setIsShowTopics(
      !(notEmpty(currentValue) || notEmpty(pendingValue) || notEmpty(routeQueryValue)),
    );
  }, [currentValue, pendingValue, routeQueryValue]);

  useEffect(() => {
    if (currentValue === routeQueryValue) return;
    refreshAdSlot();
  }, [currentValue, refreshAdSlot, routeQueryValue]);

  // The condition for tracking page view is different with refreshAdSlot
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.();
  }, [trackPageView, routeQueryValue]);

  return (
    <SearchContextProvider currentValue={currentValue} searchPageType="default">
      <PageSeo title="Search" />
      <AdContainer>
        <StyledAdSlot {...adSlotProps} />
      </AdContainer>
      <SearchContainer>
        <SearchInput
          onChange={event => {
            setPendingValue(event.target.value ?? "");
          }}
        />
        <ErrorBoundary fallback={<></>}>{isShowTopics && <SearchExploreTopics />}</ErrorBoundary>
        <SearchResultPanel />
      </SearchContainer>
    </SearchContextProvider>
  );
};

SearchPage.getInitialProps = context => ({
  animateKey: "search",
  value: parseQueryString(context.query.value) ?? "",
});

export default SearchPage;
